<template>
  <SkeletonLoaderConclusion v-if="isLoadingData" />
  <section v-else class="checkin-conclusion">
    <HeaderMessage
      :badgeMessage="badgeMessage"
      :heading="heading"
      :badgeIcon="badgeIcon"
      :badgeType="badgeType"
      ref="headerMessage"
      :showBadge="showBadge"
    />
    <SurveyButton
      v-if="showSurvey"
      :surveys="surveys"
      :hasVaccine="hasVaccineOnExams"
      :isAdmissionAuthorized="isAdmissionAuthorized"
    />

    <VCsat v-if="showCsat" />

    <InfoTicket
      ref="infoTicket-conclusionPage"
      :hasSurvey="false"
      :unit="currentUnitName"
      :brand="this.selectedBrand.name"
      :patientName="patientName"
      :scheduleDate="scheduleDate"
      :qrcode="false"
    />

    <InfoExamCategory v-if="showInfoExamCategories" :examList="listDetailExams" />
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { admissionProcessFinishedMixin, dataLayersMixin } from '@/utils/mixins';
import SurveyButton from '@/views/conclusion/partials/SurveyButton.partial.vue';
import VCsat from '@/components/Csat.vue';
import HeaderMessage from './partials/HeaderMessage';
import { LOCAL_STORAGE_ALTERNATIVE_MESSAGE } from '@/utils/constants/checkin.constants';
import SkeletonLoaderConclusion from './partials/SkeletonLoaderConclusion.partial.vue';
import moment from 'moment';
import InfoTicket from './partials/InfoTicket.partial';
import InfoExamCategory from './partials/InfoExamCategory.vue';
import { HealthInsuranceClientId } from '@/utils/enums/paymentMagicNumberEnum';

export default {
  name: 'CheckinConclusion',

  mixins: [admissionProcessFinishedMixin, dataLayersMixin],

  components: {
    VCsat,
    SurveyButton,
    SkeletonLoaderConclusion,
    InfoTicket,
    InfoExamCategory,
    HeaderMessage,
  },

  data() {
    return {
      listDetailExams: [],
      currentUnitName: '',
      patientName: '',
      scheduleDate: '',
      isLoadingData: true,
      showSurvey: false,
      showFirstAdvice: false,
      showSecondaAdvice: false,
      surveys: null,
      correctSubMessage: '',
      hasVaccineOnExams: false,
      documents: [
        {
          id: 'medical-order',
          icon: 'file-svg',
          text: 'Pedido médico original',
        },
        {
          id: 'original-doc',
          icon: 'document-svg',
          text: 'Documento original com foto (RG, CNH ou equivalentes)',
        },
      ],
      password: null,
      minimalPaddingBottom: 0,
      headerMessageStucked: false,
      alternativeMessageDataStored: localStorage.getItem(LOCAL_STORAGE_ALTERNATIVE_MESSAGE),
      erronOnTracking: false,
      hoursDifference: 0,
      isTokenAmilValid: false,
      isAmilClient: false,
      isBradescoClient: false,
      heading: '',
      badgeIcon: 'CheckSvg',
      badgeType: 'primary',
      badgeMessage: '',
      showBadge: false,
    };
  },

  computed: {
    ...mapState({
      currentAdmission: ({ currentAdmission }) => currentAdmission.data,
      currentAdmissionPayment: ({ currentAdmission }) => currentAdmission.data?.payments[0],
      mdmData: ({ mdm }) => mdm.data,
      haveAllExamsSameCategory: ({ searchExams }) => searchExams.data.haveAllExamsSameCategory,
      firstExamId: ({ searchExams }) => searchExams.data.detailedExams[0].categoria.id,
      flowSteps: ({ global }) => global.flowSteps,
      alternativeMessageData: ({ global }) => global.alternativeMessageData,
      cpfSearched: ({ cpfSearched }) => cpfSearched.data.cpf,
      medicalOrders: (state) => state.currentAdmission.data.medicalOrders,
      brandId: (state) => state.brandUnit.data.currentBrand.brandId,
      isAdmissionAuthorized: ({ global }) => global.isAdmissionAuthorized,
      atUnit: ({ global }) => global.atUnit,
      manuallySelectedExams: ({ searchExams }) => searchExams.data.manuallySelectedExams,
      healthInsuranceVariables: ({ healthInsurance }) =>
        healthInsurance.data.healthInsuranceVariables,
      specificFields: (state) => state.payments.specificFields,
      skipServiceDeskFlowFail: ({ global }) => global.skipServiceDeskFlowFail,
      endFlowMessageByFlow: ({ global }) => global.endFlowMessageByFlow,
      utmOrigin: ({ global }) => global.utmOrigin,
    }),

    ...mapGetters({
      currentUnit: 'enumUnitSelected',
      flowStepsList: 'global/flowStepsList',
      isT2FlowAllowed: 'global/isT2FlowAllowed',
      isCollaboratorFlow: 'global/isCollaboratorFlow',
      isVaccineFlow: 'global/isVaccineFlow',
      isScheduledAdmission: 'global/isScheduledAdmission',
      selectedEnum: 'enumUnitSelected',
      hasCompletedFlow: 'global/hasCompletedFlow',
      getInsurancePlan: 'getInsurancePlan',
      selectedBrand: 'selectedBrand',
      getEndMessageByFlow: 'global/getEndMessageByFlow',
      admissionFlows: 'global/admissionFlows',
      isBradescoFlowSuccess: 'global/isBradescoFlowSuccess',
    }),

    showInfoExamCategories() {
      const hasVaccine = this.isVaccineFlow && this.isCollaboratorFlow;
      return this.hasPaymentResolved || hasVaccine;
    },
    hasStartedCheckinFromQRCode() {
      return this.utmOrigin === 'qrcode';
    },
    isLemeUnit() {
      return this.brandId === 40;
    },
    isInCompanyFlow() {
      return this.admissionFlows.isInCompanyFlow;
    },
    showCsat() {
      if (
        this.currentAdmission.status === 'CHECK_IN_DONE' ||
        this.currentAdmission.status === 'ADMISSION_DONE'
      ) {
        return true;
      }

      return false;
    },
    isPaymentSuccess() {
      const paymentStatus = this.currentAdmissionPayment?.status;

      return paymentStatus === 'PAID';
    },

    hasPaymentResolved() {
      // Checks if the "payment status" (insurance authorized or private) was resolved
      return (
        this.isAdmissionAuthorized ||
        this.flowSteps.some((step) => {
          const stepStringified = JSON.stringify(step);

          return stepStringified.includes('[7,2]') || stepStringified.includes('[8,2]');
        })
      );
    },

    hasServiceTicket() {
      return !!this.password && !!this.currentUnit.magicTv;
    },
  },

  async mounted() {
    await this.getMdmPatientById(this.currentAdmission.mdmIdPatient);

    await this.initSetup();
    this.analyticsEvent();

    await this.resolveSurveyStuff();

    this.hasVaccineOnExams = await this.hasVaccineOnAdmission();

    if (this.$route.params.questionnariesDone) {
      this.showSurvey = false;
    }

    const isQuestionnariesStepChecked = this.isQuestionnariesStepAlreadyDone();

    if (isQuestionnariesStepChecked) {
      this.showSurvey = false;
    }

    this.hoursDifference = await this.returnHoursDifference(new Date(), this.examDate);

    await this.getSpecialitesAndCategoriesExams();
    await this.getScheduleDate();
    this.getUnit();
    this.getPatientName();
    this.isLoadingData = false;

    if (window.dtrum?.endSession) {
      window.dtrum.endSession();
    }
  },

  methods: {
    ...mapMutations({
      enableLoader: 'enableLoader',
      setAlternativeMessageData: 'global/SET_ALTERNATIVE_MESSAGE_DATA',
      clearAlternativeMessageData: 'global/clear_alternative_message_data',
    }),

    ...mapActions([
      'includePatientToPriorityQueue',
      'getExamsCategories',
      'getMdmPatientByCpf',
      'getMdmPatientById',
      'getEndFlowMessage',
      'getDataProviderExamsCategories',
      'global/updateFlowSteps',
      'global/setHasSurvey',
      'getUnitDocsSurveys',
      'getAdmissionById',
      'getAmilTokenValidation',
      'global/getEndMessagesByFlow',
    ]),

    async getSpecialitesAndCategoriesExams() {
      if (this.currentAdmission?.medicalOrders) {
        const { exams = [] } = this.currentAdmission?.medicalOrders[0];

        const examsCodes = exams.map((exam) => exam.examCode);
        const { data = [] } = await this.getDataProviderExamsCategories({ exams: examsCodes });
        this.listDetailExams = data;
      }
    },

    async getScheduleDate() {
      let dateFormat = 'DD/MM/YYYY - kk:mm';
      if (this.isInCompanyFlow) {
        dateFormat = 'DD/MM/YYYY';
      }

      this.scheduleDate = moment(this.currentAdmission.admissionDate).format(dateFormat);
    },

    getPatientName() {
      if (this.mdmData) {
        this.patientName = this.mdmData.name;
      }
    },
    getUnit() {
      if (!this.isInCompanyFlow) {
        if (this.selectedUnit) {
          this.currentUnitName = this.selectedUnit.nome;
        }
      }
    },

    isQuestionnariesStepAlreadyDone() {
      const foundQuestionnary = this.flowSteps.find((step) => step[0] === 11);

      if (foundQuestionnary) {
        return foundQuestionnary[1] === 2;
      }

      return false;
    },

    async handleFlowFinished() {
      const admissionStatus = this.currentAdmission.status;
      const isNotAdmissionDone = admissionStatus !== 'ADMISSION_DONE';
      const isAdmissionAuthorizedNotAmil = this.isAdmissionAuthorized && !this.isAmilClient;
      const isAdmissionAuthorizedAmilValid = this.isAdmissionAuthorized && this.isTokenAmilValid;
      const isAdmissionPaid = (this.isVaccineFlow || this.isT2FlowAllowed) && this.isPaymentSuccess;

      if (this.isBradescoClient) {
        if (!this.isBradescoFlowSuccess) {
          return;
        }
      }

      const isElegibleToBeAdmissionDone =
        this.isInCompanyFlow ||
        isAdmissionAuthorizedNotAmil ||
        isAdmissionAuthorizedAmilValid ||
        isAdmissionPaid;

      if (isElegibleToBeAdmissionDone && isNotAdmissionDone) {
        return await this.setAdmissionStatusDone();
      }
    },

    async getEndMessage() {
      await this['global/getEndMessagesByFlow']();
      const admissionStatus = this.currentAdmission.status;
      const isAdmissionDone = admissionStatus === 'ADMISSION_DONE';

      if (this.isInCompanyFlow) {
        this.showBadge = false;
        this.heading = this.endFlowMessageByFlow.isInCompanyFlow;
        return;
      }

      if (this.isCollaboratorFlow) {
        this.setSuccessBadge();
        this.heading = this.endFlowMessageByFlow.collaboratorVaccineFlow;
        return;
      }
      if (isAdmissionDone) {
        this.setSuccessBadge();
        this.heading = this.endFlowMessageByFlow.skipServiceDeskFlowSucess;
        return;
      }
      if (this.hasStartedCheckinFromQRCode) {
        this.showBadge = false;
        this.heading = this.endFlowMessageByFlow.goToServiceDeskQrCodeFlow;
        return;
      }
      if (this.skipServiceDeskFlowFail) {
        this.setWarningBadge();
        this.heading = this.endFlowMessageByFlow.skipServiceDeskFlowFail;
        return;
      }
      this.setPrimaryBadge();
      this.heading = this.endFlowMessageByFlow.goToServiceDeskFlow;
    },

    setSuccessBadge() {
      this.showBadge = true;
      this.badgeMessage = 'Pronto para atendimento';
      this.badgeIcon = 'CheckSvg';
      this.badgeType = 'success';
    },
    setPrimaryBadge() {
      this.showBadge = true;
      this.badgeMessage = 'Pré-Atendimento concluído';
      this.badgeIcon = 'CheckSvg';
      this.badgeType = 'primary';
    },
    setWarningBadge() {
      this.showBadge = true;
      this.badgeMessage = 'Retire uma senha';
      this.badgeIcon = 'TicketSvg';
      this.badgeType = 'warning';
    },

    async resolveTracking() {
      const enableLemeTracking = this.alternativeMessageDataStored && this.isLemeUnit;
      const trackingIsElegible =
        enableLemeTracking ||
        (!this.alternativeMessageDataStored && (!this.isVaccineFlow || !this.isT2FlowAllowed));

      if (trackingIsElegible) {
        const hasPreviousPwd = !!sessionStorage.getItem('checkinConclusionPassword');
        this.setPassword(hasPreviousPwd);
      }
    },

    setPassword(hasPreviousPwd, pwd) {
      if (hasPreviousPwd) {
        this.password = sessionStorage.getItem('checkinConclusionPassword');
        return;
      }

      if (pwd) {
        this.password = pwd;
        sessionStorage.setItem('checkinConclusionPassword', pwd);
        return;
      }

      this.password = null;
    },

    async initSetup() {
      try {
        this.enableLoader(true);
        this.isAmilClient = this.currentAdmissionPayment?.clientId === HealthInsuranceClientId.AMIL;
        this.isBradescoClient =
          this.currentAdmissionPayment?.clientId === HealthInsuranceClientId.BRADESCO;

        if (this.isAmilClient) {
          this.isTokenAmilValid = await this.getAmilTokenValidation();
        }
        await this.getAdmissionById();

        await this.admissionProcessFinished(false);

        if (!this.isInCompanyFlow) {
          await this.resolveTracking();
        }

        this.includePatientToPriorityQueue();

        await this.handleFlowFinished();
        await this.getEndMessage();

        if (this.hasPaymentResolved) {
          await this.getExamsCategories();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.enableLoader(false);
      }
      await this.enableLoader(false);
    },
    async hasVaccineOnAdmission() {
      if (this.currentAdmission.medicalOrders) {
        const { exams = [] } = this.currentAdmission.medicalOrders[0];
        const examsCodes = exams.map((exam) => exam.examCode);

        const { data = [] } = await this.getDataProviderExamsCategories({ exams: examsCodes });

        if (Array.isArray(data) && !!data.length) {
          const categoriesExams = data.map((ct) => ct.category?.name);
          const specialtiesExams = data.map((ct) => ct.especiality?.name);

          if (categoriesExams.includes('Vacina') || specialtiesExams.includes('VACINA')) {
            return true;
          }

          return false;
        }

        return false;
      }

      return false;
    },
    incrementSurveyUrlsWithPatientData(urlsList) {
      if (!urlsList.length) {
        return [];
      }

      const { questions = [] } = this.currentAdmission;

      let cpfValue = '';
      let birthDay = '';
      let cpfPatient = '';
      let medications = '';
      let telCompleto = '';
      let peso = '';
      let altura = '';
      let email = '';
      let emailFound = '';
      let sexoBiologico = '';
      let isTakingMedication = '';
      let cpf = null;
      const { birthDate, documents = [], name, contacts, biologicalSex } = this.mdmData;

      const findIsTakingMedication = questions.find(
        (question) => question.question === 'taking_medications',
      );

      if (Array.isArray(documents)) {
        cpf = documents.find((doc) => doc.type === 'CPF');
      }
      const medicalQuestion = questions.find((question) => question.question === 'medications');
      const weight = questions.find((question) => question.question === 'weight');
      const height = questions.find((question) => question.question === 'height');

      const telContact = contacts.find((contact) => contact.contactType === 'CELULAR');

      const principalEmail = contacts.find(
        (contact) => contact.contactType === 'EMAIL' && contact.principal,
      );

      if (findIsTakingMedication) {
        isTakingMedication = `&fazUso1160=${findIsTakingMedication.answer}`;
      }

      if (principalEmail) {
        emailFound = principalEmail;
      } else {
        emailFound = contacts.find((contact) => contact.contactType === 'EMAIL') || '';
      }

      if (!!cpf && cpf.value) {
        cpfValue = cpf.value;
        cpfPatient = `&cpfDoPaciente=${cpfValue}`;
      }

      if (telContact) {
        const telefoneArea = telContact.value.split(' ')[0];
        const telefone = telContact.value.split(' ')[1];
        telCompleto = `&telefonePara[area]=${telefoneArea}&telefonePara[phone]=${telefone}`;
      }

      if (birthDate) {
        const dateSplitted = birthDate.split('-');
        const day = dateSplitted[2];
        const month = dateSplitted[1];
        const year = dateSplitted[0];

        birthDay = `&dataDeNasc[day]=${day}&dataDeNasc[month]=${month}&dataDeNasc[year]=${year}`;
      }

      if (!!medicalQuestion && !!medicalQuestion.answer.length) {
        const answer = medicalQuestion.answer;
        medications = `&outrasInformacoes800[0][]=${answer}&fazUso210[0][2]=${answer}&quaisUso=${answer}`;
      }

      if (!!weight && !!weight.answer.length) {
        peso = `&peso=${weight.answer}`;
      }

      if (!!height && !!height.answer.length) {
        altura = `&altura=${height.answer}`;
      }

      if (biologicalSex) {
        const capitalizedBilogicalSex = `${biologicalSex[0]}${biologicalSex
          .slice(1)
          .toLowerCase()}`;
        sexoBiologico = `&sexoBiologico=${capitalizedBilogicalSex}`;
      }

      const fullName = `&nomeCompleto=${name}`;

      email = `&emailPara=${emailFound.value}`;

      return urlsList.map((survey) => ({
        name: survey.name,
        link: `${survey.link}${fullName}${cpfPatient}${telCompleto}${birthDay}${medications}${altura}${peso}${email}${sexoBiologico}${isTakingMedication}`,
      }));
    },
    async mountDasaFormLink() {
      try {
        const questionnaries = await this.getUnitDocsSurveys();
        if (questionnaries) {
          const surveyLink = {
            name: 'Questionário',
            link: questionnaries.link,
          };

          return surveyLink;
        }

        await this['global/setHasSurvey'](false);
        return null;
      } catch (err) {
        await this['global/setHasSurvey'](false);
        return null;
      }
    },
    async surveyStuff() {
      const dasaFormLink = await this.mountDasaFormLink();
      if (dasaFormLink) {
        this.surveys = dasaFormLink;
        this.showSurvey = true;
      } else {
        this.surveys = null;
        this.showSurvey = false;
        return false;
      }
    },
    async resolveSurveyStuff() {
      const exceptionOriginAUT =
        this.currentAdmission.source.origin === 'AUT' && !this.manuallySelectedExams.length;
      const surveyNotDefined =
        this.isT2FlowAllowed &&
        !this.atUnit &&
        !this.manuallySelectedExams.length &&
        this.currentAdmission.status !== 'ADMISSION_DONE';

      if ((exceptionOriginAUT || surveyNotDefined) && this.hasVaccineOnExams) {
        this.showSurvey = false;
        return;
      }

      await this.surveyStuff();
    },
    analyticsEvent() {
      this.mostCommonPagesAnalytics({
        eventType: 'funnel_step',
        currentStep: 'Finalização',
        message: this.heading,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.checkin-conclusion {
  padding-top: $gap-default;
  position: relative;
}
</style>

<style lang="scss">
.checkin-conclusion {
  .container {
    background: #f4f4f4;
    height: auto;
    min-height: 100%;
  }
}
</style>
